import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Users, Search, Loader2, ShieldCheck, Building2, User,
  ChevronLeft, ChevronRight, ExternalLink, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import walletApi from '../../lib/walletApi';

const TYPE_ICONS = { super_admin: ShieldCheck, company_admin: Building2, employee: User };
const TYPE_COLORS = {
  super_admin: 'bg-pink-50 text-pink-600',
  company_admin: 'bg-blue-50 text-blue-600',
  employee: 'bg-emerald-50 text-emerald-600'
};

const fmt = (n) => `${Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filter) params.user_type = filter;
      if (debounced) params.search = debounced;
      const data = await walletApi.get('/super-admin/all-users', { params }).then(r => r.data);
      setUsers(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages || Math.ceil(data.total / pageSize));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { setPage(1); }, [filter, debounced]);
  useEffect(() => { load(); }, [page, filter, debounced]);

  const openDetail = (u) => {
    const url = `/super-admin/user-detail/${u.user_type}/${u.id}`;
    window.open(url, '_blank');
  };

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="space-y-5" data-testid="user-management">
      <div>
        <h1 className="text-xl font-bold text-gray-900">All Users</h1>
        <p className="text-sm text-gray-500">{total.toLocaleString()} users across the platform</p>
      </div>

      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 border-gray-200" data-testid="user-search" />
        </div>
        <Select value={filter || 'all'} onValueChange={v => setFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 h-9 border-gray-200"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="super_admin">Super Admins</SelectItem>
            <SelectItem value="company_admin">Company Admins</SelectItem>
            <SelectItem value="employee">Employees</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>
      ) : (
        <>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-2.5 px-3 font-semibold text-gray-500">User</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-gray-500 hidden md:table-cell">Company</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-gray-500 hidden sm:table-cell">Department</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-gray-500">Type</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-gray-500 hidden sm:table-cell">Balance</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-gray-500 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => {
                  const Icon = TYPE_ICONS[u.user_type] || User;
                  const color = TYPE_COLORS[u.user_type] || 'bg-gray-50 text-gray-600';
                  return (
                    <tr key={`${u.user_type}-${u.id}`}
                      className="hover:bg-pink-50/30 cursor-pointer transition-colors"
                      onClick={() => openDetail(u)}
                      data-testid={`user-row-${u.id}`}>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">{u.first_name} {u.last_name}</p>
                            <p className="text-[10px] text-gray-400 truncate">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-gray-500 hidden md:table-cell">{u.company_name || '—'}</td>
                      <td className="py-2.5 px-3 text-gray-500 hidden sm:table-cell">{u.department || '—'}</td>
                      <td className="py-2.5 px-3">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${color}`}>
                          {u.user_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right hidden sm:table-cell" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {u.balance !== null && u.balance !== undefined ? (
                          <span className="text-xs text-gray-700">{fmt(u.balance)}</span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <ExternalLink className="w-3.5 h-3.5 text-gray-300 inline-block" />
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-xs">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between" data-testid="pagination">
              <p className="text-[11px] text-gray-500">
                Showing <span className="font-semibold text-gray-700">{startItem}–{endItem}</span> of{' '}
                <span className="font-semibold text-gray-700">{total.toLocaleString()}</span>
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)}
                  className="h-7 text-[10px] px-2" data-testid="page-first">First</Button>
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="h-7 w-7 p-0" data-testid="page-prev">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                      onClick={() => setPage(p)}
                      className={`h-7 w-7 p-0 text-[10px] ${p === page ? 'bg-pink-500 hover:bg-pink-600 text-white' : ''}`}>
                      {p}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="h-7 w-7 p-0" data-testid="page-next">
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}
                  className="h-7 text-[10px] px-2" data-testid="page-last">Last</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
